import React from 'react';
import { IcebergDatepicker, IcebergDatepickerProps } from '@tradesolution/iceberg-ui-react';
import DateHelper from 'utils/DateHelper';
import { Form } from 'react-bootstrap';
import {
  format,
  startOfMonth,
  startOfYear,
  endOfMonth,
  endOfYear,
  isSameDay,
  subYears,
  startOfDay,
  subMonths,
  addMonths,
} from 'date-fns';
import {
  shouldShowLastDayOfPreviousMonthSuggestion,
  shouldShowLastDayOfPreviousYearSuggestion,
  shouldShowLastDayOfCurrentMonthSuggestion,
} from './NorwegianDatepickerLogic';

interface NorwegianDatepickerProps extends Omit<IcebergDatepickerProps, 'onChange'> {
  onChange: (date: Date | undefined) => void;
  showFirstDaySuggestions?: boolean;
  showLastDaySuggestions?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** If true, suppresses suggestions until either minDate or maxDate is provided */
  loading?: boolean;
}

export const NorwegianDatepicker: React.FC<NorwegianDatepickerProps> = ({
  onChange,
  value,
  showFirstDaySuggestions,
  showLastDaySuggestions,
  minDate,
  maxDate,
  loading,
  ...rest
}) => {
  const handleDateChange = (e: React.MouseEvent, date: Date | undefined) => {
    e.preventDefault();
    const adjustedDate = DateHelper.addNorwegianTimezone(date);
    onChange(adjustedDate);
  };

  const handleIcebergDateChange = (date: Date | null | undefined) => {
    if (!date) {
      onChange(undefined);
      return;
    }
    const adjustedDate = DateHelper.addNorwegianTimezone(date);
    onChange(adjustedDate);
  };

  // Adjust the display value to show Norwegian time
  const displayValue = value ? DateHelper.addNorwegianTimezone(value) : undefined;
  const referenceDate = displayValue || new Date();

  let firstDayOfMonth = startOfMonth(referenceDate);
  const firstDayOfYear = startOfYear(referenceDate);
  const firstDayOfNextMonth = startOfMonth(addMonths(referenceDate, 1));

  // If minDate is within the same month/year and after the first day, use minDate as the "first day" suggestion
  if (
    minDate &&
    minDate.getFullYear() === referenceDate.getFullYear() &&
    minDate.getMonth() === referenceDate.getMonth() &&
    minDate > firstDayOfMonth
  ) {
    firstDayOfMonth = minDate; // keep original time (consumer handles timezone via DateHelper)
  }

  // Determine if the currently selected date (not the fallback referenceDate) is last day of its month / year
  const selectedIsLastDayOfMonth =
    !!displayValue && isSameDay(displayValue, endOfMonth(displayValue));
  const selectedIsLastDayOfYear =
    !!displayValue && displayValue.getDate() === 31 && displayValue.getMonth() === 11;

  const lastDayOfPreviousMonth = startOfDay(endOfMonth(subMonths(referenceDate, 1)));
  const lastDayOfPreviousYear = startOfDay(endOfYear(subYears(referenceDate, 1)));
  const lastDayOfCurrentMonth = startOfDay(endOfMonth(referenceDate));
  const selectedIsLastDayOfPreviousYear =
    !!displayValue && isSameDay(displayValue, lastDayOfPreviousYear);

  // Deduplication flags: avoid showing identical date twice
  const firstDayMonthEqualsFirstDayYear = isSameDay(firstDayOfMonth, firstDayOfYear);
  const lastDayPrevMonthEqualsLastDayPrevYear = isSameDay(
    lastDayOfPreviousMonth,
    lastDayOfPreviousYear,
  );
  const lastDayCurrentMonthEqualsLastDayPrevYear = isSameDay(
    lastDayOfCurrentMonth,
    lastDayOfPreviousYear,
  );

  const isWithinRange = (d: Date): boolean => {
    // If no explicit range is provided, always treat as within range
    if (!minDate && !maxDate) return true;
    if (!d) return false;
    if (minDate && d < minDate) return false;
    if (maxDate && d > maxDate) return false;
    return true;
  };

  // Build suggestions only after all date calculations & checks. Deduplicate via Set.
  const suggestions: { date: Date; label: string }[] = [];
  const added = new Set<number>();
  const addSuggestion = (d: Date | undefined, label: string) => {
    if (!d) return;
    const time = d.getTime();
    if (added.has(time)) return;
    added.add(time);
    suggestions.push({ date: d, label });
  };

  if (showFirstDaySuggestions) {
    if (
      (!displayValue || !isSameDay(displayValue, firstDayOfYear)) &&
      isWithinRange(firstDayOfYear)
    ) {
      addSuggestion(firstDayOfYear, 'Første dag i året');
    }
    if (
      !firstDayMonthEqualsFirstDayYear &&
      (!displayValue || !isSameDay(displayValue, firstDayOfMonth)) &&
      isWithinRange(firstDayOfMonth)
    ) {
      addSuggestion(firstDayOfMonth, 'Første dag i måneden');
    }
    if (
      (!displayValue || !isSameDay(displayValue, firstDayOfNextMonth)) &&
      isWithinRange(firstDayOfNextMonth)
    ) {
      addSuggestion(firstDayOfNextMonth, 'Første dag i neste måned');
    }
  }
  if (showLastDaySuggestions) {
    if (
      shouldShowLastDayOfCurrentMonthSuggestion(displayValue) &&
      isWithinRange(lastDayOfCurrentMonth)
    ) {
      addSuggestion(lastDayOfCurrentMonth, 'Siste dag i inneværende måned');
    }
    if (
      shouldShowLastDayOfPreviousMonthSuggestion(displayValue) &&
      isWithinRange(lastDayOfPreviousMonth)
    ) {
      addSuggestion(lastDayOfPreviousMonth, 'Siste dag i forrige måned');
    }
    if (
      !lastDayPrevMonthEqualsLastDayPrevYear &&
      !lastDayCurrentMonthEqualsLastDayPrevYear &&
      shouldShowLastDayOfPreviousYearSuggestion(displayValue) &&
      isWithinRange(lastDayOfPreviousYear)
    ) {
      addSuggestion(lastDayOfPreviousYear, 'Siste dag i fjor');
    }
  }

  // Show suggestions in ascending order so they don't look like random dates
  suggestions.sort((a, b) => a.date.getTime() - b.date.getTime());

  const hasRange = !!minDate || !!maxDate;
  const showSuggestions = suggestions.length > 0 && (!loading || hasRange); // Only show once all checks processed & (optionally) range ready

  return (
    <>
      <IcebergDatepicker
        {...rest}
        value={displayValue}
        minDate={minDate}
        maxDate={maxDate}
        onChange={handleIcebergDateChange}
      />
      {showSuggestions && (
        <div className="mt-2">
          <Form.Text className="fs-6">
            <span className="me-2 text-muted">Forslag:</span>
            {suggestions.map(({ date, label }) => (
              <a
                key={date.getTime()}
                href="#"
                className="me-3"
                title={label}
                onClick={(e) => handleDateChange(e, date)}
              >
                {format(date, 'dd.MM.yyyy')}
              </a>
            ))}
          </Form.Text>
        </div>
      )}
    </>
  );
};
